"use client";

import { useEffect } from "react";

/** L'identificativo del modulo del selettore di spazio. */
export const ID_MODULO_SPAZIO = "modulo-spazio";

/**
 * L'identificativo del modulo del selettore del contesto — l'anno; fino al
 * 6 ottobre 2026 anche la materia —, nato con la milestone 7a. È il secondo
 * modulo della pagina che si applica al cambio, e per questo il componente qui sotto ha imparato a sapere **quale**
 * modulo servire: prima ce n'era uno solo e il nome era scritto dentro.
 */
export const ID_MODULO_CONTESTO = "modulo-contesto";

/**
 * Fa applicare la tendina appena la scelta cambia, **senza togliere la strada
 * che funziona sempre**.
 *
 * Non disegna niente. Attacca un ascoltatore e, solo **dopo** averlo
 * attaccato, marca il modulo con `data-auto`; è il CSS a nascondere il
 * pulsante «Vai» quando quel segno c'è. Se il codice cliente non parte, il
 * segno non compare e il pulsante resta: nessuna scelta rimane senza un modo
 * di applicarla.
 *
 * **L'ascoltatore sta sul modulo, non sulla tendina, e qui c'è una lezione.**
 * La prima versione si agganciava alla tendina, presa una volta sola alla
 * partenza. Ma la tendina porta una `key` legata allo spazio corrente, quindi
 * dopo ogni cambio React la **ricostruisce da capo**: l'ascoltatore restava
 * attaccato a un elemento che non esisteva più, e il secondo cambio non lo
 * sentiva nessuno. Funzionava una volta sola — segnalato dal proprietario in
 * collaudo il 6 settembre 2026, mezz'ora dopo la correzione che l'aveva
 * causato.
 *
 * Il modulo invece non viene mai ricostruito, e l'evento di cambio **risale**
 * fino a lui da qualunque campo ci sia dentro. Agganciarsi al contenitore
 * stabile invece che al figlio che cambia è la regola generale.
 */
export function InviaAlCambio({ idModulo = ID_MODULO_SPAZIO }: { idModulo?: string } = {}) {
  useEffect(() => {
    const modulo = document.getElementById(idModulo);
    if (!(modulo instanceof HTMLFormElement)) {
      return;
    }

    const applica = () => modulo.requestSubmit();
    const pulsante = modulo.querySelector('button[type="submit"]');

    modulo.addEventListener("change", applica);

    // Si nasconde **il pulsante**, con l'attributo `hidden` del DOM, e non si
    // marca il modulo lasciando che sia una regola di stile a farlo sparire.
    //
    // La prima versione faceva così — un `data-auto` sul modulo più una regola
    // `display: none` nel foglio — e sul telefono del proprietario il pulsante
    // restava visibile **anche se il cambio automatico funzionava**, cioè anche
    // se quella riga era stata eseguita. Ho verificato che la regola arrivava
    // davvero nel foglio servito e che il markup era quello giusto: non ho
    // capito perché non avesse effetto.
    //
    // Quando una funzione dipende da tre cose che devono combaciare — un
    // attributo, un selettore, un foglio caricato — e una delle tre non
    // combacia senza dirlo, la risposta non è indagare più a fondo: è togliere
    // le tre cose e lasciarne una. `hidden` non ha bisogno di nessun foglio di
    // stile per funzionare.
    if (pulsante instanceof HTMLElement) {
      pulsante.hidden = true;
    }

    return () => {
      modulo.removeEventListener("change", applica);
      if (pulsante instanceof HTMLElement) {
        pulsante.hidden = false;
      }
    };
  }, [idModulo]);

  return null;
}
